import { datadogRum } from '@datadog/browser-rum';
import { useSession } from '@side/next-auth/react';
import { useEffect, useMemo } from 'react';
import { useSelfTeamMemberships } from '@/common/hooks/useSelfTeamMemberships';
import { buildSearchFilterQuery } from '@/common/utils/buildSearchFilterQuery';
import {
  type AgentProfileSummaryFields,
  mapTeamMemberToAgentProfile,
} from '$agent-profiles';
import { useIdentity } from '$api-clients/hooks';

const DEFAULT_LIMIT = 500;
const EMPTY_FILTERS: string[] = [];

interface UseSubmitAsTeamMembersOptions {
  search?: string;
  /** DSL clauses for the identity `q` param, e.g. from `useAgentDslFilters`. */
  filters?: string[];
  limit?: number;
  skip?: boolean;
}

export const useSubmitAsTeamMembers = ({
  search = '',
  filters = EMPTY_FILTERS,
  limit = DEFAULT_LIMIT,
  skip,
}: UseSubmitAsTeamMembersOptions = {}) => {
  const { data: session } = useSession();
  const userId = session?.user?.id;

  const q = useMemo(
    () => buildSearchFilterQuery(search, filters),
    [search, filters],
  );

  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useIdentity(
    '/users/{userId}/interaction-permissions/{type}/team-members',
    !skip && userId
      ? {
          params: {
            path: { userId, type: 'submit-as' },
            query: { limit, expand: 'team', ...(q ? { q } : {}) },
          },
        }
      : null,
    { keepPreviousData: true },
  );

  // Overflow would silently hide people from submit-as options, so report it.
  const overflowed = Boolean(permissionsData?.paging.next);
  useEffect(() => {
    if (!overflowed) return;

    datadogRum.addAction('Submit-as team members exceeded one page', {
      limit,
      userId,
    });
  }, [overflowed, limit, userId]);

  // The permission endpoint returns explicit grants only, but users can always
  // submit as themselves — merge in the session user's own memberships.
  const {
    memberships: selfMemberships,
    loading: selfLoading,
    error: selfError,
  } = useSelfTeamMemberships({ q, skip });

  const data = useMemo(() => {
    if (!permissionsData && !selfMemberships) return undefined;

    const grantedAgents = (permissionsData?.data ?? []).reduce<
      AgentProfileSummaryFields[]
    >((acc, member) => {
      if (member.team) {
        acc.push(mapTeamMemberToAgentProfile({ ...member, team: member.team }));
      }
      return acc;
    }, []);

    const grantedKeys = new Set(
      grantedAgents.map(
        (agent) => `${agent.userIdentityId}:${agent.teamIdentityId}`,
      ),
    );

    const selfAgents = (selfMemberships ?? []).reduce<
      AgentProfileSummaryFields[]
    >((acc, membership) => {
      if (!grantedKeys.has(`${membership.userId}:${membership.team.id}`)) {
        acc.push(mapTeamMemberToAgentProfile(membership));
      }
      return acc;
    }, []);

    return [...grantedAgents, ...selfAgents].sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || ''),
    );
  }, [permissionsData, selfMemberships]);

  return {
    data,
    loading: permissionsLoading || selfLoading,
    error: permissionsError ?? selfError,
  };
};
